<template>
  <div class="max-w-4xl mx-auto">
    <!-- Loading Modal -->
    <LoadingModal
      :is-visible="showLoadingModal"
      :title="loadingTitle"
      :message="loadingMessage"
      :current-agent="currentAgent"
      :current-agent-number="currentAgentNumber"
      :completed-agents="completedAgents"
    />

    <div class="bg-white shadow rounded-lg">
      <div class="px-6 py-8">
        <div class="text-center mb-12">
          <div class="mb-6">
            <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl shadow-lg mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <h1 class="text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              Carga de Documentos de Laboratorio
            </h1>
            <p class="text-gray-600 text-lg font-light leading-relaxed max-w-3xl mx-auto">
              Sistema de análisis diagnóstico avanzado con procesamiento mediante inteligencia artificial médica certificada.
            </p>
          </div>
          <div class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full border border-blue-200">
            <div class="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
            <p class="text-sm text-blue-700 font-medium uppercase tracking-wider">
              SABIOLAB Medical AI Platform
            </p>
          </div>
        </div>

        <!-- Upload Area -->
        <div class="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-all duration-300 cursor-pointer group"
             :class="{ 'border-blue-500 bg-blue-50': isDragOver }"
             @drop="handleDrop"
             @dragover="handleDragOver"
             @dragleave="handleDragLeave"
             @click="triggerFileInput">
          
          <input
            ref="fileInput"
            type="file"
            accept=".pdf,.docx"
            @change="handleFileSelect"
            class="hidden"
          />

          <div v-if="!selectedFile" class="space-y-8">
            <div class="mx-auto w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform duration-300">
              <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            <div class="space-y-3">
              <p class="text-2xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Arrastre su documento aquí</p>
              <p class="text-gray-600 text-lg font-light">o seleccione desde el explorador de archivos</p>
            </div>
            <div class="pt-6 border-t border-gray-200 space-y-3">
              <div class="flex items-center justify-center space-x-6 text-sm text-gray-500">
                <div class="flex items-center">
                  <svg class="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                  PDF, DOCX
                </div>
                <div class="flex items-center">
                  <svg class="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                  Máx. 10MB
                </div>
              </div>
              <p class="text-xs text-gray-400 uppercase tracking-wider font-medium">Transferencia Segura • Protocolo Encriptado</p>
            </div>
          </div>

          <div v-else class="space-y-6">
            <div class="mx-auto w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center shadow-lg">
              <svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="space-y-2">
              <p class="text-xl font-bold text-gray-900">{{ selectedFile.name }}</p>
              <p class="text-gray-500 text-lg">{{ formatFileSize(selectedFile.size) }}</p>
            </div>
            <button
              @click.stop="removeFile"
              class="inline-flex items-center px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors font-medium"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
              Eliminar archivo
            </button>
          </div>
        </div>

        <!-- Upload Button -->
        <div v-if="selectedFile" class="mt-10 flex justify-center">
          <button
            @click="uploadReport('v2')"
            :disabled="reportStore.isLoading"
            class="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold text-lg"
          >
            <svg v-if="!reportStore.isLoading" class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
            <svg v-else class="w-5 h-5 mr-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            <span v-if="!reportStore.isLoading">Procesar con IA Médica</span>
            <span v-else>Procesando...</span>
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="reportStore.hasError" class="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div class="flex">
            <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="ml-3">
              <p class="text-sm text-red-800">{{ reportStore.error }}</p>
            </div>
          </div>
        </div>

        <!-- Recent Reports -->
        <div v-if="reportStore.hasReports" class="mt-12">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">Reportes Recientes</h2>
          <div class="space-y-3">
            <div
              v-for="report in reportStore.reports.slice(0, 5)"
              :key="report.id"
              class="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div>
                <p class="font-medium text-gray-900">
                  {{ report.patientName || 'Sin nombre' }}
                </p>
                <p class="text-sm text-gray-500">
                  {{ formatDate(report.createdAt) }}
                </p>
              </div>
              <div class="flex items-center space-x-2">
                <span
                  :class="{
                    'bg-green-100 text-green-800': report.status === 'completed',
                    'bg-yellow-100 text-yellow-800': report.status === 'processing',
                    'bg-red-100 text-red-800': report.status === 'failed'
                  }"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ getStatusText(report.status) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useReportStore } from '../stores/reportStore'
import { useToast } from 'vue-toastification'
import LoadingModal from '../components/LoadingModal.vue'

const router = useRouter()
const reportStore = useReportStore()
const toast = useToast()

const fileInput = ref<HTMLInputElement>()
const selectedFile = ref<File | null>(null)
const isDragOver = ref(false)

// Loading modal state
const showLoadingModal = ref(false)
const loadingTitle = ref('Procesando Reporte')
const loadingMessage = ref('Analizando resultados de laboratorio con IA...')
const currentAgent = ref('')
const currentAgentNumber = ref(0)
const completedAgents = ref(0)

onMounted(async () => {
  // Load recent reports
  try {
    await reportStore.listReports()
  } catch (error) {
    console.error('Error loading reports:', error)
  }
})

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    selectedFile.value = target.files[0]
  }
}

const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = false
  
  if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
    selectedFile.value = event.dataTransfer.files[0]
  }
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = true
}

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = false
}

const removeFile = () => {
  selectedFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const uploadReport = async (version: 'v1' | 'v2') => {
  if (!selectedFile.value) return

  try {
    // Mostrar modal de loading
    showLoadingModal.value = true
    loadingTitle.value = `Procesando Reporte (${version.toUpperCase()})`
    loadingMessage.value = 'Iniciando análisis de laboratorio con IA...'
    completedAgents.value = 0
    currentAgent.value = ''
    currentAgentNumber.value = 0

    if (version === 'v2') {
      // Mapeo de nombres del backend a nombres del frontend
      const agentNameMap: { [key: string]: string } = {
        'Conversión a Imágenes': 'Extractor', // La conversión es parte del Agente A, no se muestra
        'Agente Extractor (A)': 'Extractor',
        'Agente Extractor con Visión (A)': 'Extractor',
        'Agente Validator (B)': 'Validador',
        'Agente Clinical (C)': 'Clinico',
        'Agente Auditor (D)': 'Auditor',
        'Agente Corrector (E)': 'Corrector',
        'Agente Patient (F)': 'Paciente',
        'Agente Quality Control (G)': 'Control'
      }

      // Usar polling para v2 con progreso en tiempo real
      await reportStore.uploadReportV2WithPolling(selectedFile.value, (progress: { step: number; agent: string; status: string }) => {
        console.log('🔄 Progreso recibido:', progress)
        // Actualizar UI con progreso real del backend
        // Ajustar el número de paso: si es paso 1 (conversión), lo saltamos y mostramos como paso 1 (Agente A)
        // El paso 1 del backend (conversión) se integra con el paso 2 (Agente A)
        let displayStep = progress.step
        if (progress.step === 1 && (progress.agent === 'Conversión a Imágenes' || progress.agent.includes('Conversión'))) {
          // La conversión es parte del Agente A, no la mostramos como paso separado
          return // No actualizar UI para el paso de conversión
        } else if (progress.step > 1) {
          // Ajustar: paso 2 del backend = paso 1 del frontend, paso 3 = paso 2, etc.
          displayStep = progress.step - 1
        }
        currentAgentNumber.value = displayStep
        currentAgent.value = agentNameMap[progress.agent] || progress.agent
        
        if (progress.status === 'processing') {
          loadingMessage.value = `Procesando ${currentAgent.value}...`
          console.log(`🔄 Procesando paso ${progress.step}: ${currentAgent.value}`)
        } else if (progress.status === 'completed') {
          // Marcar agente como completado
          // Si es el paso de conversión, no lo contamos
          if (progress.step === 1 && (progress.agent === 'Conversión a Imágenes' || progress.agent.includes('Conversión'))) {
            return // No contar el paso de conversión
          }
          // Ajustar el número de paso completado
          let completedStep = progress.step > 1 ? progress.step - 1 : progress.step
          completedAgents.value = completedStep
          loadingMessage.value = `${currentAgent.value} completado ✓`
          console.log(`✅ Completado paso ${completedStep}: ${currentAgent.value}`)
        }
      })
    } else {
      // v1 sin SSE (modo antiguo con simulación)
      const agentNames = [
        'Extractor',
        'Validador',
        'Clinico',
        'Auditor',
        'Corrector',
        'Paciente',
        'Control'
      ]

      const progressInterval = setInterval(() => {
        if (completedAgents.value < 7) {
          currentAgent.value = agentNames[completedAgents.value]
          currentAgentNumber.value = completedAgents.value + 1
          completedAgents.value++
        }
      }, 2000)

      await reportStore.uploadReport(selectedFile.value)
      
      clearInterval(progressInterval)
      
      // Completar todos los agentes para v1
      completedAgents.value = 7
    }

    // Mostrar estado final
    currentAgent.value = 'Proceso Completado'
    loadingMessage.value = 'Reporte procesado exitosamente ✓'
    
    // Esperar un momento para mostrar el estado final
    await new Promise(resolve => setTimeout(resolve, 800))

    // Ocultar modal
    showLoadingModal.value = false

    toast.success(`Reporte procesado exitosamente (${version})`)
    
    // Forzar recarga de la lista de reportes desde el servidor
    // Esto asegura que se muestre el nuevo reporte en la lista
    await reportStore.listReports()
    
    // Navigate to reports list
    router.push('/reports')
  } catch (error) {
    showLoadingModal.value = false
    toast.error('Error al procesar el reporte')
    console.error('Upload error:', error)
  }
}


const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusText = (status: string): string => {
  const statusMap = {
    completed: 'Completado',
    processing: 'Procesando',
    failed: 'Error'
  }
  return statusMap[status as keyof typeof statusMap] || status
}
</script>
